import { useEffect, useState } from 'react';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'communication.theme';

/**
 * Read the initial theme without ever assuming a DOM API exists. Used
 * both at hook init and at first-paint via the inline preload script in
 * index.html. The contract is identical in both places so they can't
 * disagree:
 *
 *   1. localStorage[STORAGE_KEY] wins if it equals 'dark' or 'light'
 *      — the operator's explicit choice persists across reloads.
 *   2. otherwise, prefers-color-scheme: dark at the OS level → dark.
 *   3. fall back to light. batch-184 — the console is now LIGHT-first
 *      (the redesign default); dark stays a one-click toggle and the OS
 *      dark preference is still honoured.
 *
 * Exported so the inline preload script and tests can re-use the same
 * logic without duplicating string literals.
 */
export function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = window.localStorage?.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // Some browsers throw on localStorage in privacy modes; treat as
    // "no preference" and fall through to system / default.
  }
  if (typeof window.matchMedia === 'function') {
    try {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    } catch {
      // matchMedia throws in some embedded contexts; ignore.
    }
  }
  return 'light';
}

/** Push the theme onto the <html> element so CSS variable overrides apply. */
export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
  // Pair color-scheme so native form controls / scrollbars follow suit.
  document.documentElement.style.colorScheme = theme;
}

/**
 * Hook returning the current theme + setter. The setter persists to
 * localStorage and updates the <html data-theme="…"> attribute
 * synchronously. Calling code can rely on a single source of truth.
 *
 * On mount, the hook *re-applies* the theme even if `readInitialTheme()`
 * already matched what the preload script set — this is a defensive
 * no-op in production but lets tests render the component fresh and
 * still see data-theme set on the root element.
 */
export function useTheme(): { theme: Theme; setTheme: (next: Theme) => void; toggle: () => void } {
  const [theme, setThemeState] = useState<Theme>(() => readInitialTheme());

  useEffect(() => {
    applyTheme(theme);
    try {
      window.localStorage?.setItem(STORAGE_KEY, theme);
    } catch {
      // Best-effort persistence.
    }
  }, [theme]);

  return {
    theme,
    setTheme: setThemeState,
    toggle: () => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')),
  };
}

/** Test-only: reset persisted preference between cases. */
export const THEME_STORAGE_KEY = STORAGE_KEY;
